import { useEffect, useState } from "react";
import { CircleLoader } from "../../components/CircleLoader";
import { ISubject } from "../../pages/Subject";
import {
  useCreateSubjectMutation,
  useUpdateSubjectMutation,
} from "../../hooks/useAdmin";
import { BookOpen, Hash, AlignLeft, X, Save } from "lucide-react";

interface Props {
  singleSubject: ISubject | null;
  close: () => void;
}

export const SubjectForm = ({ singleSubject, close }: Props) => {
  const add = useCreateSubjectMutation({ close });
  const edit = useUpdateSubjectMutation({ close });

  const [inputValue, setInputValue] = useState({
    name: "",
    code: "",
    description: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setInputValue((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (singleSubject) {
      setInputValue({
        name: singleSubject.name || "",
        code: singleSubject.code || "",
        description: singleSubject.description || "",
      });
    }
  }, [singleSubject]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.name) return;

    if (singleSubject) {
      edit.mutate({
        subjectId: singleSubject.id,
        payload: inputValue,
      });
    } else {
      add.mutate(inputValue);
    }
  };

  const isSaving = edit?.isPending || add?.isPending;
  const labelClass =
    "text-xs font-black text-slate-500 uppercase tracking-widest mb-2 block ml-1";
  const inputClass =
    "w-full h-12 px-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:border-blue-500 focus:bg-white outline-none transition-all text-slate-700 font-medium placeholder:text-slate-300 shadow-inner";

  return (
    <div className="w-full max-w-2xl mx-auto bg-white rounded-[2.5rem] shadow-2xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in-95 duration-300">
      <form onSubmit={handleSubmit} className="p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Subject Name */}
          <div className="space-y-1">
            <label className={labelClass}>Subject Name</label>
            <div className="relative">
              <BookOpen
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300"
                size={18}
              />
              <input
                type="text"
                name="name"
                placeholder="e.g. Mathematics"
                value={inputValue.name}
                onChange={handleChange}
                className={`${inputClass} pl-12`}
                required
              />
            </div>
          </div>

          {/* Subject Code */}
          <div className="space-y-1">
            <label className={labelClass}>Subject Code</label>
            <div className="relative">
              <Hash
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300"
                size={18}
              />
              <input
                type="text"
                name="code"
                placeholder="e.g. MAT101"
                value={inputValue.code}
                onChange={handleChange}
                className={`${inputClass} pl-12`}
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="space-y-1">
          <label className={labelClass}>Description</label>
          <div className="relative">
            <AlignLeft
              className="absolute left-4 top-5 text-slate-300"
              size={18}
            />
            <textarea
              name="description"
              placeholder="Provide a brief overview of this subject's scope..."
              value={inputValue.description}
              onChange={handleChange}
              className={`${inputClass} pl-12 pt-4 min-h-[120px] resize-none h-auto`}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-4 pt-4">
          <button
            type="button"
            onClick={close}
            className="flex-1 h-14 rounded-2xl font-black uppercase tracking-widest text-slate-400 hover:bg-slate-50 transition-all border-2 border-transparent"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={isSaving}
            className="flex-[2] h-14 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-widest shadow-xl shadow-slate-200 hover:bg-black hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-3"
          >
            {isSaving ? (
              <>
                <CircleLoader />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <Save size={18} />
                <span>{singleSubject ? "Update Changes" : "Save Subject"}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
